import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Link,
} from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import { Field, Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { IconButton } from 'src/components/atoms/IconButton';
import { Icon } from 'src/components/atoms/Icon';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';
import { FormikField } from './types';
import { validateEmail, validatePassword } from './validators';

type SignInProps = {
  email?: string;
  onSubmit: (email: string, password: string) => Promise<void>;
  onForgotPassword: () => Promise<void>;
};

/**
 * This component shows the basic sign in form for a user. For the
 * sign in flows,  the email and passwords are managed in the
 * Auth UI container
 */
export const SignIn: FC<SignInProps> = ({
  email,
  onSubmit,
  onForgotPassword,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const errorMessageColor = useFontErrorColor();

  return (
    <Formik
      initialValues={{
        email: email || '',
        password: '',
      }}
      onSubmit={async (values) => {
        try {
          setErrorMessage(null);
          await onSubmit(values.email, values.password);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          }
        }
      }}>
      {(props) => (
        <Form style={{ width: '100%' }}>
          <Field name="email" validate={validateEmail}>
            {({ field, form }: FormikField) => (
              <FormControl
                mb="24px"
                isInvalid={form.errors.email && form.touched.email}>
                <FormLabel htmlFor="email" color="gray.500" fontSize="sm">
                  Email
                </FormLabel>
                <Input
                  {...field}
                  id="email"
                  placeholder="Enter Email"
                  _placeholder={{ color: 'gray.400' }}
                  borderRadius="md"
                  border="none"
                  backgroundColor="surface.hover"
                />
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.email}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          <Field name="password" validate={validatePassword}>
            {({ field, form }: FormikField) => (
              <FormControl
                mb="24px"
                isInvalid={form.errors.password && form.touched.password}>
                <FormLabel htmlFor="password" color="gray.500" fontSize="sm">
                  Password
                </FormLabel>
                <InputGroup>
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Enter Password"
                    _placeholder={{ color: 'gray.400' }}
                    border="none"
                    backgroundColor="surface.hover"
                    borderRadius="md"
                    pr="4.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme="gray">
                      <Icon
                        iconName={showPassword ? 'view-off' : 'view'}
                        color="gray.500"
                      />
                    </IconButton>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.password}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          {errorMessage && <ErrorNotifBox title={errorMessage} />}
          <Flex direction="row" gap={4} mt={6}>
            <Link
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="50%"
              fontFamily="marketingPage"
              fontSize="16px"
              fontWeight="semibold"
              onClick={onForgotPassword}
              color="gray.500"
              _hover={{ textDecoration: 'underline' }}>
              Reset Password
            </Link>
            <Button
              buttonName="sign-in"
              isLoading={props.isSubmitting}
              type="submit"
              width="50%"
              bg="icon.green"
              borderRadius="8px"
              color="white"
              fontFamily="marketingPage"
              fontSize="16px"
              fontWeight="semibold"
              _hover={{ opacity: 0.7 }}>
              Log In
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
